import { Button, Divider, Group, Image, Stack, Stepper, Text, Title } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { Content } from "shared/components/global/Content";
import { AddEudPanel } from "shared/components/opt-in/AddEudPanel";
import { ContactMethodsPanel } from "shared/components/opt-in/ContactMethodsPanel";
import { createOptInUserFormState, OptInUserFormProvider, useOptInUserForm } from "shared/components/opt-in/context";
import { RegisterPanel } from "shared/components/opt-in/RegisterPanel";
import { TopicsPanel } from "shared/components/opt-in/TopicsPanel";
import { useCustomer } from "shared/graphql/cust";
import { features } from "shared/graphql/features";
import { useFormState } from "shared/hooks/useFormState";
import z from "zod";

const Page = () => {
    const { data: customer } = useCustomer();
    const hasTopics = features.enabled("HAS_TOPICS");

    const form = useOptInUserForm({
        initialValues: createOptInUserFormState({ customer, user: undefined }),
        validate: zodResolver(
            z.object({
                durationDays: z.string(),
                agreedToPrivacyPolicy: z.boolean().refine((it) => it == true, { message: "You must agree to the Privacy Policy" }),
                agreedToTOS: z.boolean().refine((it) => it == true, { message: "You must agree to the Terms of Service" }),
            }),
        ),
    });

    const [step, setStep] = useFormState(form, "meta.step");
    const [userId] = useFormState(form, "id");

    return (
        <Content paper>
            <Group w="100%" justify="center">
                <Image
                    my="md"
                    src={`${import.meta.env.VITE_API_BASE_URL}/customers/${customer.id}/icon`}
                    fallbackSrc="/bryx.svg"
                    style={{ maxWidth: 50, maxHeight: 50 }}
                />
                <Title order={1}>{customer.name}</Title>
            </Group>
            <Divider mx="-sm" />
            <OptInUserFormProvider form={form}>
                <Stepper active={step} allowNextStepsSelect={false}>
                    <Stepper.Step>
                        <RegisterPanel />
                    </Stepper.Step>
                    <Stepper.Step allowStepSelect={step >= 1}>
                        <Stack>
                            <ContactMethodsPanel />
                            <AddEudPanel />
                            <Divider mx="-sm" />

                            <Group justify="flex-end">
                                <Button variant="default" leftSection={<IconChevronLeft />} onClick={() => setStep(step - 1)}>
                                    Back
                                </Button>
                                <Button
                                    disabled={form.getValues().euds.filter((it) => it.isVerified).length == 0}
                                    rightSection={<IconChevronRight />}
                                    type="submit"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (form.validate().hasErrors) return;
                                        setStep(step + 1);
                                    }}
                                >
                                    Continue
                                </Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>
                    {hasTopics && (
                        <Stepper.Step>
                            <Stack>
                                <TopicsPanel />
                                <Divider mx="-sm" />

                                <Group justify="flex-end">
                                    <Button variant="default" leftSection={<IconChevronLeft />} onClick={() => setStep(step - 1)}>
                                        Back
                                    </Button>
                                    <Button
                                        rightSection={<IconChevronRight />}
                                        onClick={() => {
                                            setStep(step + 1);
                                        }}
                                    >
                                        Continue
                                    </Button>
                                </Group>
                            </Stack>
                        </Stepper.Step>
                    )}
                    <Stepper.Step>
                        <Stack>
                            <Stack gap={0}>
                                <Title order={4}>Sign up complete!</Title>
                                <Text>You are signed up to receive alerts! You may now close this page.</Text>
                            </Stack>
                            <Text>To continue managing your account, click the button below</Text>
                            <Group justify="flex-end">
                                <Link to="/opt-in/$userId" params={{ userId: userId! }}>
                                    <Button>Manage</Button>
                                </Link>
                            </Group>
                        </Stack>
                    </Stepper.Step>
                </Stepper>
            </OptInUserFormProvider>
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/opt-in/_layout/register")({
    component: Page,
});
